import React, { useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import styles from './AdsBanner.module.scss';
import { Phrases } from '../../utilities/constants/phrases';
import { logError } from '../../utilities/helpers/errors';

const INITIAL_CHECK_INTERVAL = 200;
const MAX_ATTEMPTS = 20;

export interface Props {
  adClient?: string;
  adSlot?: string;
  adFormat?: string;
  fullWidthResponsive?: boolean;
  style?: React.CSSProperties;
}

const AdsBanner: React.FC<Props> = ({
  adClient = process.env.REACT_APP_ADSENSE_CLIENT || '',
  adSlot = '',
  adFormat,
  fullWidthResponsive,
  style,
}) => {
  const isInitialized = useRef(false);
  const adRef = useRef<HTMLModElement>(null);
  const attemptCount = useRef(0);
  const [isAdFilled, setIsAdFilled] = useState(false);

  const handleAdSensePush = useCallback(() => {
    if (
      typeof window === 'object' &&
      window.adsbygoogle &&
      window.adsbygoogle.loaded &&
      adRef.current &&
      !isInitialized.current
    ) {
      requestAnimationFrame(() => {
        if (!adRef.current || isInitialized.current) return;

        const rect = adRef.current.getBoundingClientRect();
        const computedStyle = window.getComputedStyle(adRef.current);

        const hasValidSize =
          rect.width > 0 &&
          rect.height > 0 &&
          computedStyle.display !== 'none' &&
          computedStyle.visibility !== 'hidden';

        if (hasValidSize) {
          try {
            (window.adsbygoogle = window.adsbygoogle || []).push({});
            isInitialized.current = true;
          } catch (error) {
            logError(error, {
              message: 'Error pushing AdSense',
            });
          }
        }
      });
    }
  }, []);

  useEffect(() => {
    if (!adClient || !adSlot || isInitialized.current) {
      return;
    }

    attemptCount.current = 0;
    let checkAdSenseInterval: NodeJS.Timeout | null = null;

    const clearAdSenseInterval = () => {
      if (checkAdSenseInterval) {
        clearInterval(checkAdSenseInterval);
        checkAdSenseInterval = null;
      }
    };

    const initializeAdSense = () => {
      try {
        handleAdSensePush();
        if (!isInitialized.current) {
          checkAdSenseInterval = setInterval(() => {
            attemptCount.current += 1;
            if (attemptCount.current >= MAX_ATTEMPTS) {
              clearAdSenseInterval();
              return;
            }
            handleAdSensePush();
            if (isInitialized.current) {
              clearAdSenseInterval();
            }
          }, INITIAL_CHECK_INTERVAL);
        }
      } catch (error) {
        logError(error, {
          message: 'Error initializing AdSense',
        });
      }
    };

    initializeAdSense();

    // eslint-disable-next-line consistent-return
    return () => clearAdSenseInterval();
  }, [adClient, adSlot, handleAdSensePush]);

  useEffect(() => {
    if (!adRef.current) {
      return;
    }

    const adElement = adRef.current;

    const updateAdFilledState = () => {
      const status = adElement.getAttribute('data-ad-status');
      setIsAdFilled(status === 'filled');
    };

    updateAdFilledState();

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (
          mutation.type === 'attributes' &&
          mutation.attributeName === 'data-ad-status'
        ) {
          updateAdFilledState();
        }
      });
    });

    observer.observe(adElement, {
      attributes: true,
      attributeFilter: ['data-ad-status'],
    });

    // eslint-disable-next-line consistent-return
    return () => {
      observer.disconnect();
    };
  }, []);

  if (!adClient || !adSlot) {
    return null;
  }

  return (
    <div className='w-100'>
      <p className='ta-center mb-xxxxs'>{Phrases.ADVERTISEMENT_TITLE}</p>
      <div
        className={classNames(
          { [styles.adsBannerBg]: !isAdFilled },
          'd-flex flex-jc-c flex-ai-c'
        )}>
        <ins
          ref={adRef}
          className='adsbygoogle'
          style={style}
          data-ad-client={adClient}
          data-ad-slot={adSlot}
          data-ad-format={adFormat}
          data-full-width-responsive={fullWidthResponsive ? 'true' : 'false'}
        />
      </div>
    </div>
  );
};

export default AdsBanner;
